import React, { useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import DownCaretIcon from 'common/Icons/DownCaretIcon';
import Dropdown from 'components/lib/Dropdown';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { DataType } from 'store/slices/globalSlice';

import { useSelectAdvancedWithGroupingContext } from './SelectAdvancedWithGroupingContext';
import SelectionRow from './SelectionRow';

const TriggerButtonContainer = styled.div`
  padding: 8px 10px 7px 10px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  border: solid 1px var(--line-gray);

  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  font-size: 14px;
  width: 185px;
`;

const StyledMenu = styled.div`
  .ant-dropdown-menu {
    padding: 0;
    border-radius: 0;
    border: 1px solid var(--line-gray);
    border-bottom: 0;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    width: 183px;
    box-sizing: content-box;

    .ant-dropdown-menu-item {
      font-size: 14px;
      padding: 0;
      border-bottom: 1px solid var(--line-gray);
    }
  }
`;

interface Props {
  style?: React.CSSProperties;
  apartmentTypes: DataType[];
}

const DropdownWithCheckboxSelection = ({ style, apartmentTypes }: Props) => {
  const { selectedGroupOptions, addItemsToSelectedGroupOptions } = useSelectAdvancedWithGroupingContext();
  const { apartmentTypes: currentApartmentTypes } = useCommunityViewContext();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOptions =
    selectedGroupOptions?.filter((option) => apartmentTypes.find((i: any) => i.id === option)) ?? [];
  const onReset = () => addItemsToSelectedGroupOptions(apartmentTypes.map((i: any) => i.id));

  const handleClick = (event: React.MouseEvent) => {
    // Simulate click on the document to close other dropdowns if there is any open
    document.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    // Prevent selecting/unselecting the whole group of options. Just open/close the dropdown.
    event.stopPropagation();
    setDropdownOpen(!dropdownOpen);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  const dropdownMenuItems =
    apartmentTypes?.map((apartmentType: DataType) => {
      const checked = selectedOptions.some((selectedOptionId) => selectedOptionId === apartmentType.id);
      return {
        key: apartmentType.id,
        label: (
          <SelectionRow
            id={apartmentType.id}
            checked={checked}
            label={apartmentType.type}
            disabled={selectedOptions.length === 1 && checked}
          />
        ),
      };
    }) ?? [];
  dropdownMenuItems.push({
    key: 0,
    label: (
      <div
        style={{ padding: '6px 10px' }}
        onClick={(e) => {
          onReset();
          e.stopPropagation();
        }}
      >
        Reset
      </div>
    ),
  });

  let label = 'No items selected';
  if (selectedOptions.length === apartmentTypes.length) {
    label = 'All room variants';
  } else if (selectedOptions.length === 1) {
    label = '1 room variant';
  } else {
    label = `${selectedOptions.length} room variants`;
  }

  return (
    <div ref={dropdownRef}>
      <Dropdown
        trigger={['click']}
        open={dropdownOpen}
        menu={{ items: dropdownMenuItems }}
        dropdownRender={(menu) => <StyledMenu onMouseDown={(e) => e.stopPropagation()}>{menu}</StyledMenu>}
        overlayStyle={{
          width: '183px',
        }}
        placement="bottomLeft"
      >
        <TriggerButtonContainer style={style} onClick={handleClick}>
          <div>{label}</div>
          <DownCaretIcon />
        </TriggerButtonContainer>
      </Dropdown>
    </div>
  );
};

export default DropdownWithCheckboxSelection;
